import gsap from 'gsap';
import AbstractComponent from './AbstractComponent';

class SVGAnimation extends AbstractComponent {
  initializeBeforeEvents() {
    this.timeline = this.getTimeline();
    super.initializeBeforeEvents();
  }

  handleIntersection([entry]) {
    if (entry.isIntersecting || entry.intersectionRatio > this.options.intersection.threshold) {
      this.timeline.restart();
    } else {
      this.timeline.pause();
    }
  }

  getUI() {
    const ui = super.getUI();
    const { id } = ui.$element.dataset;

    switch (id) {
      case 'sunshine':
      case 'storm':
      case 'brain':
        ui.$lines = [...ui.$element.querySelectorAll('.line')];
        ui.$circle = ui.$element.querySelector('.circle');
        return ui;
      case 'water-drops':
        ui.$drop = ui.$element.querySelector('.drop');
        ui.$wavesTop = [...ui.$element.querySelectorAll('.waves-top path')];
        ui.$wavesBottom = [...ui.$element.querySelectorAll('.waves-bottom path')];
        return ui;
      case 'skin-pimple':
        ui.$circle = ui.$element.querySelector('.circle');
        return ui;
      case 'stomach':
      case 'benefits-points':
        ui.$move = ui.$element.querySelector('.dot-move');
        ui.$path = ui.$element.querySelector('.dot-path');
        return ui;
      case 'circles':
        ui.$move = ui.$element.querySelector('.circle-move');
        ui.$top = ui.$element.querySelector('.circle-top');
        ui.$middle = ui.$element.querySelector('.circle-middle');
        ui.$bottom = ui.$element.querySelector('.circle-bottom');
        return ui;
      case 'heart':
      case 'benefits-circles':
        ui.$left = ui.$element.querySelector('.left');
        ui.$right = ui.$element.querySelector('.right');
        return ui;
      case 'benefits-earth':
        ui.$earth = ui.$element.querySelector('.earth');
        return ui;
      case 'collagene':
        ui.$circles = ui.$element.querySelectorAll('circle');
        return ui;
      case 'less-stress':
      case 'skin-protect':
        ui.$line = ui.$element.querySelector('.dot-path');
        return ui;
      default:
        return ui;
    }
  }

  getTimeline() {
    const { id } = this.ui.$element.dataset;

    switch (id) {
      case 'sunshine':
      case 'storm':
      case 'brain':
        return this.getTimelineSunshine();
      case 'water-drops':
        return this.getTimelineWaterDrops();
      case 'skin-pimple':
        return this.getTimelineSkinPimple();
      case 'stomach':
        return this.getTimelineStomach();
      case 'circles':
        return this.getTimelineCircles();
      case 'heart':
        return this.getTimelineHeart();
      case 'benefits-circles':
        return this.getTimelineBenefitsCircles();
      case 'benefits-points':
        return this.getTimelineBenefitsPoints();
      case 'benefits-earth':
        return this.getTimelineBenefitsEarth();
      case 'collagene':
        return this.getTimelineCollagene();
      case 'less-stress':
        return this.getTimelineLessStress();
      case 'skin-protect':
        return this.getTimelineSkinProtect();
      default:
        return gsap.timeline({ paused: true });
    }
  }

  getTimelineSunshine() {
    const timeline = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 1.5 });
    const { $circle, $lines } = this.ui;

    /* eslint-disable */
    timeline
      .set($circle, { scale: 0, transformOrigin: '50% 50%' })
      .set($lines, { drawSVG: '100% 60%' })
      .to($lines, { drawSVG: '100% 100%', duration: 0.3 }, 0.1)
      .fromTo($lines, { drawSVG: '30% 40%' }, { drawSVG: '100% 50%', duration: 0.5, }, '+=0.1')
      .to($lines, { drawSVG: '100% 60%', duration: 0.5, }, '-=0.2')
      .to($circle, { scale: 1, }, '-=0.4');
    /* eslint-enable */

    timeline.progress(0.01);

    return timeline;
  }

  getTimelineWaterDrops() {
    const { $drop, $wavesTop, $wavesBottom } = this.ui;
    const [$firstWaveTop, $secondWaveTop] = $wavesTop;
    const [$firstWaveBottom, $secondWaveBottom] = $wavesBottom;

    /* eslint-disable */
    const wavesTween = gsap.to([$firstWaveTop, $firstWaveBottom], { x: "-100%", ease: "none", duration: 6, repeat: -1, });
    const wavesCloneTweens = gsap.to([$secondWaveTop, $secondWaveBottom], { x: "0%", ease: "none", duration: 6, repeat: -1, });

    const timeline = gsap.timeline({ paused: true })
      .set([$secondWaveTop, $secondWaveBottom], { x: "100%" })
      .fromTo($drop, {
        y: 0
      }, {
        y: 40,
        duration: 1.5,
        yoyo: true,
        repeat: -1,
        repeatDelay: 1.5,
        ease: "expo.inOut"
      })
      .fromTo(
        [$firstWaveTop, $secondWaveTop, $firstWaveBottom, $secondWaveBottom],
        { opacity: 0 },
        { opacity: 1, duration: 0.4 },
        0.5
      )
      .add(wavesTween, 0.5)
      .add(wavesCloneTweens, 0.5);
    /* eslint-enable */

    return timeline;
  }

  getTimelineSkinPimple() {
    const { $circle } = this.ui;

    /* eslint-disable */
    return gsap
      .timeline({ paused: true })
      .to($circle, { rotate: 360, transformOrigin: "50%", ease: "none", repeat: -1, duration: 10, }, 0)
      .to($circle, { scale: 0.9, repeat: -1, duration: 1, yoyoEase: true, yoyo: true, }, 0);
    /* eslint-enable */
  }

  getTimelineStomach() {
    const { $move, $path } = this.ui;
    return gsap.timeline({ paused: true, repeat: -1, repeatDelay: 1.5 }).to($move, {
      duration: 5,
      ease: 'power1.inOut',
      motionPath: {
        path: $path,
        align: $path,
        alignOrigin: [0.5, 0.5],
      },
    });
  }

  getTimelineCircles() {
    const { $top, $middle, $bottom, $move } = this.ui;

    const topRadius = parseFloat($bottom.getAttribute('r'), 10);
    const middleScale = topRadius / parseFloat($middle.getAttribute('r'), 10);
    const bottomScale = topRadius / parseFloat($top.getAttribute('r'), 10);

    return gsap
      .timeline({ paused: true, repeat: -1, repeatDelay: 1.5 })
      .set($middle, { opacity: 0 })
      .set($bottom, { opacity: 0 })
      .to($move, {
        scale: bottomScale,
        transformOrigin: '50% 100%',
        duration: 1.5,
        onUpdate() {
          const scale = gsap.getProperty($move, 'scale');
          const middleOpacity = gsap.getProperty($middle, 'opacity');
          const bottomOpacity = gsap.getProperty($bottom, 'opacity');
          if (scale <= middleScale && middleOpacity === 0) {
            gsap.set($middle, { opacity: 1 });
          } else if (scale <= bottomScale && bottomOpacity === 0) {
            gsap.set($bottom, { opacity: 1 });
          }
        },
      });
  }

  getTimelineHeart() {
    const { $left, $right } = this.ui;

    /* eslint-disable */
    return gsap
      .timeline({ paused: true, repeat: -1, yoyo: true, repeatDelay: 1.5 })
      .to($left, { x: -21.5, ease: "power2.out", duration: 1 })
      .to($right, { x: 21.5, ease: "power2.out", duration: 1 }, 0);
    /* eslint-enable */
  }

  getTimelineBenefitsCircles() {
    const { $left, $right } = this.ui;

    return gsap
      .timeline({ paused: true, repeat: -1, yoyo: true, repeatDelay: 1.5 })
      .to($left, { x: -12, duration: 1, ease: 'quad.inOut' }, 0)
      .to($right, { x: 12, duration: 1, ease: 'quad.inOut' }, 0);
  }

  getTimelineBenefitsPoints() {
    const { $move, $path } = this.ui;
    return gsap.timeline({ paused: true, repeat: -1, repeatDelay: 1.5 }).to($move, {
      duration: 5,
      ease: 'power1.inOut',
      motionPath: {
        path: $path,
        align: $path,
        alignOrigin: [0.5, 0.5],
      },
    });
  }

  getTimelineBenefitsEarth() {
    const { $earth } = this.ui;
    return gsap.timeline({ paused: true }).to($earth, {
      duration: 10,
      repeat: -1,
      ease: 'none',
      rotate: 360,
      transformOrigin: '50% 50%',
    });
  }

  getTimelineCollagene() {
    const { $circles } = this.ui;
    const DIST = 25;

    return gsap.timeline({ paused: true }).to(
      $circles,
      {
        x: (i) => (i === 0 ? 0 : i === 1 ? DIST : -DIST) + '%',
        y: (i) => (i > 0 ? DIST : -DIST) + '%',
        duration: 2,
        ease: 'quad.inOut',
      },
      0
    );
  }

  getTimelineLessStress() {
    const { $line } = this.ui;

    return gsap
      .timeline({ paused: true })
      .fromTo(
        $line,
        { drawSVG: '0% 100%' },
        { drawSVG: '100% 100%', duration: 2, ease: 'quad.inOut' },
        0
      );
  }

  getTimelineSkinProtect() {
    const { $line } = this.ui;

    return gsap
      .timeline({ paused: true })
      .fromTo($line, { drawSVG: '100%' }, { drawSVG: '0%', duration: 2, ease: 'quad.inOut' }, 0);
  }
}

export default SVGAnimation;
